/**
 * State package - Centralized Zustand store for client-side game state
 * Server-authoritative design: this store is a rendering cache only
 */

import { shallow } from "zustand/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { createStore } from "zustand/vanilla";
import type {
  Asteroid,
  Bullet,
  Mine,
  Pellet,
  Player,
} from "../../protocol/src/generated/messages";
import { INTERPOLATION_CONFIGS, interpolateEntities } from "./interpolation";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Party member information
 */
export interface PartyMember {
  id: string;
  name: string;
  steamId?: string;
  ready: boolean;
}

/**
 * Party state for multiplayer lobbies
 */
export interface Party {
  id: string;
  joinCode: string;
  leader: string;
  members: PartyMember[];
}

/**
 * CTF flag state
 */
export interface Flag {
  id: string;
  teamId: number;
  pos: { x: number; y: number };
  state: "home" | "carried" | "dropped";
  carrierId?: string;
  droppedAt?: number;
}

/**
 * MOBA base state
 */
export interface MOBABase {
  id: string;
  teamId: number;
  pos: { x: number; y: number };
  health: number;
  maxHealth: number;
  radius: number;
}

/**
 * MOBA NPC state
 */
export interface MOBANPC {
  id: string;
  teamId: number;
  laneId: string;
  pos: { x: number; y: number };
  vel: { x: number; y: number };
  health: number;
  maxHealth: number;
  radius: number;
}

/**
 * Player final statistics at game end
 */
export interface PlayerFinalStats {
  playerId: string;
  playerName: string;
  kills: number;
  deaths: number;
  pelletsCollected: number;
  score: number;
  teamId?: number;
  captures: number;
}

/**
 * Game end data displayed when a game concludes
 */
export interface GameEndData {
  isWinner: boolean;
  isDraw: boolean;
  reason: string;
  finalScores: Record<number, number>;
  playerStats: PlayerFinalStats[];
  localTeamId?: number;
  localPlayerId?: string;
}

// ============================================================================
// STATE SCHEMA
// ============================================================================

/**
 * Complete client-side game state
 * Separates authoritative server state from interpolated rendering state
 */
export interface GameState {
  // --- Metadata ---
  /** Current player's ID (null if not in game) */
  playerId: string | null;
  /** Current lobby ID (null if not in lobby) */
  lobbyId: string | null;
  /** Team mode of current lobby ("ffa", "teams", "ctf", "custom", null if not in lobby) */
  lobbyTeamMode: "ffa" | "teams" | "ctf" | "custom" | null;
  /** Whether the game has started */
  gameStarted: boolean;
  /** Timestamp of last server update (for staleness detection) */
  lastServerUpdate: number;
  /** Party state (null if not in party) */
  party: Party | null;
  /** Connection status string (OFFLINE, CONNECTED, ERROR, etc.) */
  connectionStatus: string;
  /** Whether connection is established */
  isConnected: boolean;

  // --- UI Visibility State ---
  /** Whether the AuthPanel should be visible */
  showAuthPanel: boolean;
  /** Whether the PartyPanel should be visible */
  showPartyPanel: boolean;
  /** Whether the LobbyBrowser should be visible */
  showLobbyBrowser: boolean;

  // --- Authoritative State (from server) ---
  /** Server-authoritative player states */
  players: Map<string, Player>;
  /** Server-authoritative bullet states */
  bullets: Map<string, Bullet>;
  /** Server-authoritative asteroid states */
  asteroids: Map<string, Asteroid>;
  /** Server-authoritative pellet states */
  pellets: Map<string, Pellet>;
  /** Server-authoritative mine states */
  mines: Map<string, Mine>;
  /** Server-authoritative flag states (CTF mode) */
  flags: Map<string, Flag>;
  /** Server-authoritative MOBA base states (MOBA mode) */
  mobaBases: Map<string, MOBABase>;
  /** Server-authoritative MOBA NPC states (MOBA mode) */
  mobaNpcs: Map<string, MOBANPC>;
  /** MOBA wave number (MOBA mode) */
  mobaWaveNumber: number;
  /** Game mode state (null if not in a game mode) */
  gameModeState: {
    mode: string;
    teamScores?: Record<number, number>;
    captureLimit?: number;
    flags?: Flag[];
  } | null;
  /** Game end data (null if game has not ended) */
  gameEndData: GameEndData | null;

  // --- Interpolated State (for smooth rendering) ---
  /** Interpolated player positions for rendering */
  interpolatedPlayers: Map<string, Player>;
  /** Interpolated bullet positions for rendering */
  interpolatedBullets: Map<string, Bullet>;
  /** Interpolated asteroid positions for rendering */
  interpolatedAsteroids: Map<string, Asteroid>;
  /** Interpolated pellet positions for rendering */
  interpolatedPellets: Map<string, Pellet>;
}

/**
 * Initial state values
 */
const INITIAL_STATE: GameState = {
  // Metadata
  playerId: null,
  lobbyId: null,
  lobbyTeamMode: null,
  gameStarted: false,
  lastServerUpdate: 0,
  party: null,
  connectionStatus: "OFFLINE",
  isConnected: false,

  // UI Visibility
  showAuthPanel: true,
  showPartyPanel: true,
  showLobbyBrowser: true,

  // Authoritative state
  players: new Map(),
  bullets: new Map(),
  asteroids: new Map(),
  pellets: new Map(),
  mines: new Map(),
  flags: new Map(),
  mobaBases: new Map(),
  mobaNpcs: new Map(),
  mobaWaveNumber: 0,
  gameModeState: null,
  gameEndData: null,

  // Interpolated state
  interpolatedPlayers: new Map(),
  interpolatedBullets: new Map(),
  interpolatedAsteroids: new Map(),
  interpolatedPellets: new Map(),
};

// ============================================================================
// ACTIONS INTERFACE
// ============================================================================

/**
 * Delta update payload for incremental state changes
 * All fields are optional - only send what changed
 */
export interface DeltaUpdate {
  timestamp: number;
  // Player updates
  createdPlayers?: Player[];
  updatedPlayers?: Player[];
  deletedPlayerIds?: string[];
  // Bullet updates
  createdBullets?: Bullet[];
  updatedBullets?: Bullet[];
  deletedBulletIds?: string[];
  // Asteroid updates
  createdAsteroids?: Asteroid[];
  updatedAsteroids?: Asteroid[];
  deletedAsteroidIds?: string[];
  // Pellet updates
  createdPellets?: Pellet[];
  updatedPellets?: Pellet[];
  deletedPelletIds?: string[];
  // Mine updates
  createdMines?: Mine[];
  // Flag updates (full replace for simplicity)
  flags?: Flag[];
  updatedMines?: Mine[];
  deletedMineIds?: string[];
}

/**
 * Full state snapshot payload for complete state replacement
 */
export interface FullStateUpdate {
  players: Player[];
  bullets: Bullet[];
  asteroids: Asteroid[];
  pellets: Pellet[];
  mines: Mine[];
  flags?: Flag[];
  timestamp: number;
}

/**
 * Actions for manipulating game state
 * All mutations are immutable - create new Maps/objects instead of mutating
 */
export interface GameActions {
  /**
   * Set global metadata fields (playerId, lobbyId, lobbyTeamMode, gameStarted, party, connectionStatus, isConnected, showAuthPanel, showPartyPanel, showLobbyBrowser, gameModeState, gameEndData)
   * @param state - Partial metadata to update
   */
  setGlobalState: (
    state: Partial<
      Pick<
        GameState,
        | "playerId"
        | "lobbyId"
        | "lobbyTeamMode"
        | "gameStarted"
        | "party"
        | "connectionStatus"
        | "isConnected"
        | "showAuthPanel"
        | "showPartyPanel"
        | "showLobbyBrowser"
        | "gameModeState"
        | "gameEndData"
      >
    >,
  ) => void;

  /**
   * Apply a full state snapshot from the server
   * Replaces all authoritative state and initializes interpolation
   * @param payload - Complete server state snapshot
   */
  applyFullState: (payload: FullStateUpdate) => void;

  /**
   * Apply a delta update from the server
   * Incrementally updates authoritative state with created/updated/deleted entities
   * @param delta - Delta update with entity changes
   */
  applyDeltaState: (delta: DeltaUpdate) => void;

  /**
   * Run interpolation for all entity types
   * Updates interpolated state to smoothly approach authoritative state
   * @param now - Current timestamp
   * @param world - World dimensions for wrapping calculations
   */
  runInterpolation: (now: number, world: { w: number; h: number }) => void;

  /**
   * Reset game state to initial values
   * Preserves playerId and party across resets
   */
  resetGameState: () => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Immutably update a map with new/updated items
 * Creates a new Map instance instead of mutating the existing one
 *
 * @param map - Current map
 * @param items - Items to add or update
 * @param keyField - Field to use as the map key
 * @returns New map with updates applied
 */
function updateMap<K, V>(
  map: Map<K, V>,
  items: V[],
  keyField: keyof V,
): Map<K, V> {
  // Create new map starting with existing entries
  const newMap = new Map(map);

  // Add or update items
  for (const item of items) {
    const key = item[keyField] as unknown as K;
    newMap.set(key, item);
  }

  return newMap;
}

/**
 * Immutably delete items from a map
 * Creates a new Map instance instead of mutating the existing one
 *
 * @param map - Current map
 * @param ids - IDs to delete
 * @returns New map with deletions applied
 */
function deleteFromMap<K, V>(map: Map<K, V>, ids: K[]): Map<K, V> {
  // Create new map starting with existing entries
  const newMap = new Map(map);

  // Delete items
  for (const id of ids) {
    newMap.delete(id);
  }

  return newMap;
}

/**
 * Convert array of entities to Map keyed by ID
 * @param items - Array of entities with id field
 * @returns Map of entities keyed by id
 */
function arrayToMap<T extends { id: string }>(items: T[]): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    map.set(item.id, item);
  }
  return map;
}

// ============================================================================
// STORE CREATION
// ============================================================================

/**
 * Centralized game state store using Zustand vanilla
 * Provides server-authoritative state management with client-side interpolation
 */
export const gameStateStore = createStore<GameState & { actions: GameActions }>(
  // biome-ignore lint/complexity/noExcessiveLinesPerFunction: Debug logging temporarily exceeds limit
  (set) => ({
    ...INITIAL_STATE,

    actions: {
      setGlobalState: (
        state: Partial<
          Pick<
            GameState,
            | "playerId"
            | "lobbyId"
            | "lobbyTeamMode"
            | "gameStarted"
            | "party"
            | "connectionStatus"
            | "isConnected"
            | "showAuthPanel"
            | "showPartyPanel"
            | "showLobbyBrowser"
            | "gameModeState"
            | "gameEndData"
          >
        >,
      ) => {
        set((current) => ({
          ...current,
          ...state,
        }));
      },

      applyFullState: (payload: FullStateUpdate) => {
        set((current) => {
          // DEBUG: Log flag data
          if (payload.flags && payload.flags.length > 0) {
            console.log(
              "[STORE] applyFullState received flags:",
              payload.flags,
            );
          }

          // Convert arrays to maps
          const players = arrayToMap(payload.players);
          const bullets = arrayToMap(payload.bullets);
          const asteroids = arrayToMap(payload.asteroids);
          const pellets = arrayToMap(payload.pellets);
          const mines = arrayToMap(payload.mines);
          const flags = payload.flags
            ? arrayToMap(payload.flags)
            : current.flags;

          // Initialize interpolated state on first update
          const shouldInitializeInterpolation =
            current.interpolatedPlayers.size === 0;

          return {
            ...current,
            players,
            bullets,
            asteroids,
            pellets,
            mines,
            flags,
            lastServerUpdate: payload.timestamp,
            // Initialize interpolated state with current state (no smoothing on first frame)
            interpolatedPlayers: shouldInitializeInterpolation
              ? new Map(players)
              : current.interpolatedPlayers,
            interpolatedBullets: shouldInitializeInterpolation
              ? new Map(bullets)
              : current.interpolatedBullets,
            interpolatedAsteroids: shouldInitializeInterpolation
              ? new Map(asteroids)
              : current.interpolatedAsteroids,
            interpolatedPellets: shouldInitializeInterpolation
              ? new Map(pellets)
              : current.interpolatedPellets,
          };
        });
      },

      applyDeltaState: (delta: DeltaUpdate) => {
        set((current) => {
          // Apply player deltas
          let players = current.players;
          if (delta.createdPlayers !== undefined) {
            players = updateMap(players, delta.createdPlayers, "id");
          }
          if (delta.updatedPlayers !== undefined) {
            players = updateMap(players, delta.updatedPlayers, "id");
          }
          if (delta.deletedPlayerIds !== undefined) {
            players = deleteFromMap(players, delta.deletedPlayerIds);
          }

          // Apply bullet deltas
          let bullets = current.bullets;
          if (delta.createdBullets !== undefined) {
            bullets = updateMap(bullets, delta.createdBullets, "id");
          }
          if (delta.updatedBullets !== undefined) {
            bullets = updateMap(bullets, delta.updatedBullets, "id");
          }
          if (delta.deletedBulletIds !== undefined) {
            bullets = deleteFromMap(bullets, delta.deletedBulletIds);
          }

          // Apply asteroid deltas
          let asteroids = current.asteroids;
          if (delta.createdAsteroids !== undefined) {
            asteroids = updateMap(asteroids, delta.createdAsteroids, "id");
          }
          if (delta.updatedAsteroids !== undefined) {
            asteroids = updateMap(asteroids, delta.updatedAsteroids, "id");
          }
          if (delta.deletedAsteroidIds !== undefined) {
            asteroids = deleteFromMap(asteroids, delta.deletedAsteroidIds);
          }

          // Apply pellet deltas
          let pellets = current.pellets;
          if (delta.createdPellets !== undefined) {
            pellets = updateMap(pellets, delta.createdPellets, "id");
          }
          if (delta.updatedPellets !== undefined) {
            pellets = updateMap(pellets, delta.updatedPellets, "id");
          }
          if (delta.deletedPelletIds !== undefined) {
            pellets = deleteFromMap(pellets, delta.deletedPelletIds);
          }

          // Apply mine deltas
          let mines = current.mines;
          if (delta.createdMines !== undefined) {
            mines = updateMap(mines, delta.createdMines, "id");
          }
          if (delta.updatedMines !== undefined) {
            mines = updateMap(mines, delta.updatedMines, "id");
          }
          if (delta.deletedMineIds !== undefined) {
            mines = deleteFromMap(mines, delta.deletedMineIds);
          }

          // Apply flag updates (full replace for simplicity)
          const flags = delta.flags ? arrayToMap(delta.flags) : current.flags;

          return {
            ...current,
            players,
            bullets,
            asteroids,
            pellets,
            mines,
            flags,
            lastServerUpdate: delta.timestamp,
          };
        });
      },

      runInterpolation: (_now: number, world: { w: number; h: number }) => {
        set((current) => {
          // Interpolate all entity types using their specific configs
          const interpolatedPlayers = interpolateEntities(
            current.players,
            current.interpolatedPlayers,
            INTERPOLATION_CONFIGS.player!,
            world,
          );

          const interpolatedBullets = interpolateEntities(
            current.bullets,
            current.interpolatedBullets,
            INTERPOLATION_CONFIGS.bullet!,
            world,
          );

          const interpolatedAsteroids = interpolateEntities(
            current.asteroids,
            current.interpolatedAsteroids,
            INTERPOLATION_CONFIGS.asteroid!,
            world,
          );

          const interpolatedPellets = interpolateEntities(
            current.pellets,
            current.interpolatedPellets,
            INTERPOLATION_CONFIGS.pellet!,
            world,
          );

          return {
            ...current,
            interpolatedPlayers,
            interpolatedBullets,
            interpolatedAsteroids,
            interpolatedPellets,
          };
        });
      },

      resetGameState: () => {
        set((current) => ({
          ...INITIAL_STATE,
          // Preserve connection-level state and user context across game resets
          playerId: current.playerId,
          party: current.party,
          connectionStatus: current.connectionStatus,
          isConnected: current.isConnected,
          // Preserve UI visibility state across game resets
          showAuthPanel: current.showAuthPanel,
          showPartyPanel: current.showPartyPanel,
          showLobbyBrowser: current.showLobbyBrowser,
          // Clear game end data when joining new lobby
          gameEndData: null,
        }));
      },
    },
  }),
);

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * React hook for accessing game state with shallow equality comparison
 * Prevents unnecessary re-renders when selected state hasn't changed
 *
 * @param selector - Function to select a slice of state
 * @returns Selected state slice
 *
 * @example
 * ```tsx
 * // Select player count (primitive - auto shallow comparison)
 * const playerCount = useGameState((s) => s.players.size);
 *
 * // Select multiple values (object - needs shallow comparison)
 * const { playerId, gameStarted } = useGameState((s) => ({
 *   playerId: s.playerId,
 *   gameStarted: s.gameStarted,
 * }));
 * ```
 */
export function useGameState<T>(
  selector: (state: GameState & { actions: GameActions }) => T,
): T {
  return useStoreWithEqualityFn(gameStateStore, selector, shallow);
}

/**
 * Vanilla API for non-React contexts (Canvas rendering, game loop)
 * Provides direct access to store methods
 */
export const { getState, setState, subscribe } = gameStateStore;

/**
 * Convenience export for actions
 * Use this for imperative updates outside React components
 *
 * @example
 * ```ts
 * // Apply server update in WebSocket handler
 * gameActions.applyFullState(payload);
 *
 * // Run interpolation in game loop
 * gameActions.runInterpolation(performance.now(), WORLD);
 * ```
 */
export const gameActions = gameStateStore.getState().actions;
